import { useForm } from 'react-hook-form';
import {
  ClassificationGroupView,
  ClassificationType,
  CreateClassifiedObjectRequest,
} from '../../../../generated/ApiClient';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { EnhancedDialog } from '../../../../component/EnhancedDialog';
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FormTextField } from '../../../../component/form/FormTextField';
import { FormDropdown } from '../../../../component/form/FormDropdown';
import RestHttpClient from '../../../../common/RestHttpClient';

export function AssignObjectDialog({
  isOpen,
  onClose,
  group,
}: {
  isOpen: boolean;
  onClose: () => void;
  group: ClassificationGroupView | null;
}) {
  const { t } = useTranslation();
  const schema = z.object({
    id: z.string().min(1),
    type: z.nativeEnum(ClassificationType),
  });

  type FormValues = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const types = Object.values(ClassificationType).map((x) => ({
    label: t(`classification.classifiedObject.types.${x}`),
    value: x,
  }));

  const onFormSubmit = async (data: FormValues) => {
    if (group) {
      RestHttpClient.createClassifiedObject(
        new CreateClassifiedObjectRequest({
          objectId: data.id,
          objectType: data.type,
          classificationGroupId: group.id,
        })
      );

      onClose();
    }
  };

  return (
    <EnhancedDialog isDialogOpened={isOpen} setIsDialogOpened={onClose}>
      <DialogTitle>{t('classification.groups.addGroup')}</DialogTitle>
      <DialogContent dividers>
        <form id="feature-form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
          <Grid container spacing={3} sx={{ paddingY: 2 }}>
            <Grid item xs={12}>
              <FormTextField
                control={control}
                name="id"
                label={t('classification.classifiedObject.id')}
                disabled={isSubmitting}
                shrink
              />
            </Grid>
            <Grid item xs={12}>
              <FormDropdown
                control={control}
                name="type"
                label={t('classification.classifiedObject.type')}
                disabled={isSubmitting}
                options={types}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions>
        <Box sx={{ textAlign: 'right' }}>
          <Button
            onClick={onClose}
            sx={{ border: 1, borderColor: 'black', color: 'black', marginRight: '4px' }}
            disabled={isSubmitting}
          >
            {t('cancel')}
          </Button>
          <Button type="submit" form="feature-form" variant="contained" disabled={isSubmitting}>
            {t('save')}
          </Button>
        </Box>
      </DialogActions>
    </EnhancedDialog>
  );
}
